import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SavedTradiesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedTradies, setSavedTradies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedTradies();
  }, []);

  const fetchSavedTradies = async () => {
    try {
      const response = await axios.get(`${API}/favorites/tradies`);
      setSavedTradies(response.data.tradies || []);
    } catch (error) {
      console.error('Failed to fetch saved tradies:', error);
      toast.error('Failed to load saved tradies');
    } finally {
      setLoading(false);
    }
  };

  const unsaveTradie = async (tradieId) => {
    try {
      await axios.delete(`${API}/favorites/tradies/${tradieId}`);
      toast.success('Removed from favorites');
      fetchSavedTradies();
    } catch (error) {
      console.error('Failed to unsave:', error);
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <Skeleton className="h-48" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="saved-tradies-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-foreground">Saved Listings</h1>
          <p className="text-muted-foreground">Your favorite tradies for quick access</p>
        </div>

        {savedTradies.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center space-y-4">
              <div className="text-6xl">❤️</div>
              <h3 className="text-2xl font-semibold text-foreground">No saved tradies yet</h3>
              <p className="text-muted-foreground">
                Browse services and click the heart icon to save your favorite tradies
              </p>
              <Button onClick={() => navigate('/tradies/browse')} className="rounded-xl">
                Browse Services
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {savedTradies.map((tradie) => (
            <Card key={tradie.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent-green/20 flex items-center justify-center text-2xl">
                      👷
                    </div>
                    <div>
                      <h3 className="font-semibold">{tradie.business_name}</h3>
                      <p className="text-sm text-muted-foreground capitalize">{tradie.tradie_type}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      unsaveTradie(tradie.id);
                    }}
                    className="text-destructive hover:bg-destructive/10 p-2 rounded-lg transition-colors"
                    data-testid={`unsave-${tradie.id}`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {tradie.abn_status === 'verified' && <Badge className="bg-green-500 text-white text-xs">✓ ABN</Badge>}
                  {tradie.licence_status === 'verified' && <Badge className="bg-green-500 text-white text-xs">✓ Licence</Badge>}
                  {tradie.insurance_status === 'verified' && <Badge className="bg-green-500 text-white text-xs">✓ Insurance</Badge>}
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2">
                  {tradie.bio || 'No bio provided'}
                </p>

                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/tradies/${tradie.id}`)}
                    variant="outline"
                    className="flex-1 rounded-xl"
                  >
                    View Profile
                  </Button>
                  <Button
                    onClick={() => navigate('/messages')}
                    className="flex-1 rounded-xl bg-primary hover:bg-primary-hover"
                  >
                    Message
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default SavedTradiesPage;
